/**
 * Geocoding utilities for converting addresses to coordinates
 * Uses Google Maps JavaScript API Geocoder (supports HTTP referrer-restricted keys)
 */

const GOOGLE_API_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY

/**
 * Load the Google Maps JavaScript API dynamically
 * Returns a promise that resolves when the API is ready
 */
let mapsApiPromise = null

export function loadMapsApi() {
  if (mapsApiPromise) return mapsApiPromise

  if (window.google?.maps?.Geocoder) {
    mapsApiPromise = Promise.resolve()
    return mapsApiPromise
  }

  if (!GOOGLE_API_KEY) {
    return Promise.reject(new Error('Missing Google Maps API key'))
  }

  mapsApiPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script')
    script.src = `https://maps.googleapis.com/maps/api/js?key=${GOOGLE_API_KEY}&libraries=geocoding`
    script.async = true
    script.onload = resolve
    script.onerror = () => reject(new Error('Failed to load Google Maps API'))
    document.head.appendChild(script)
  })

  return mapsApiPromise
}

let geocoderInstance = null

function getGeocoder() {
  if (!geocoderInstance) {
    geocoderInstance = new window.google.maps.Geocoder()
  }
  return geocoderInstance
}

/**
 * Geocode a single address to lat/lng coordinates
 * @param {string} address - The address to geocode
 * @returns {Promise<{lat: number, lng: number, formattedAddress: string} | null>}
 */
export async function geocodeAddress(address) {
  if (!address || !GOOGLE_API_KEY) {
    console.warn('Geocoding: Missing address or API key')
    return null
  }

  try {
    await loadMapsApi()
    const geocoder = getGeocoder()

    const response = await geocoder.geocode({ address })

    if (response.results?.length > 0) {
      const location = response.results[0].geometry.location
      return {
        lat: location.lat(),
        lng: location.lng(),
        formattedAddress: response.results[0].formatted_address
      }
    }

    console.warn(`Geocoding: No results for address "${address}"`)
    return null
  } catch (error) {
    if (error.code === 'ZERO_RESULTS') {
      console.warn(`Geocoding: No results for address "${address}"`)
      return null
    }
    console.error('Geocoding failed:', error)
    return null
  }
}

/**
 * Geocode multiple addresses in batch
 * Includes rate limiting to avoid API quota issues
 * @param {Array<{id: string, address: string}>} items - Items with addresses to geocode
 * @returns {Promise<Map<string, {lat: number, lng: number}>>} Map of id to coordinates
 */
export async function geocodeAddresses(items) {
  const results = new Map()

  for (const item of items) {
    if (!item.address) continue

    const coords = await geocodeAddress(item.address)
    if (coords) {
      results.set(item.id, coords)
    }

    // Rate limit: 50 requests per second max, we'll do 10/sec to be safe
    await new Promise(resolve => setTimeout(resolve, 100))
  }

  return results
}

/**
 * Cache for geocoded addresses to avoid repeated API calls
 */
const geocodeCache = new Map()

/**
 * Geocode with caching - checks cache before making API call
 * @param {string} address - The address to geocode
 * @returns {Promise<{lat: number, lng: number} | null>}
 */
export async function geocodeAddressCached(address) {
  if (!address) return null

  // Normalise address for cache key
  const cacheKey = address.toLowerCase().trim()

  if (geocodeCache.has(cacheKey)) {
    return geocodeCache.get(cacheKey)
  }

  const coords = await geocodeAddress(address)
  if (coords) {
    geocodeCache.set(cacheKey, coords)
  }

  return coords
}

/**
 * Clear the geocode cache
 */
export function clearGeocodeCache() {
  geocodeCache.clear()
}
